import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Subject, of } from 'rxjs';
import { catchError, take, takeUntil } from 'rxjs/operators';

import { enumHtsChatScope } from '@data-layer/enums/common';
import { HtsChatEntryLineModel, HtsChatEntryModel } from '@data-layer/models/common';
import { HtsChatRepository } from '@data-layer/repositories';
import { HtsAssistantHistoryService } from '../../services/hts-assistant-history.service';

/**
 * "Classification assistant history" row / button (#13385). Opens the read-only history drawer for the entry; the admin and
 * the Broker Review variants (#13548) start on the final conversation with the "watch the do-over" toggle. Shown only when the
 * entry has at least one conversation (a silent entry-level read; the entry model carries no such flag).
 */
@Component({
  selector: 'app-hts-assistant-history-link',
  templateUrl: './hts-assistant-history-link.component.html',
  styleUrls: ['./hts-assistant-history-link.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HtsAssistantHistoryLinkComponent implements OnChanges, OnDestroy {
  @Input() public entrySummaryId: number;
  @Input() public scope: enumHtsChatScope = enumHtsChatScope.Importer;
  /** Customs broker pages: the history is read through the broker management routes (#13385). */
  @Input()
  public set isBroker(isBroker: boolean) {
    if (isBroker) {
      this.scope = enumHtsChatScope.Broker;
    }
  }
  @Input() public isDoOverToggle: boolean = false;

  public isVisible: boolean = false;

  private isDestroy: Subject<void> = new Subject<void>();

  constructor(
    private htsAssistantHistoryService: HtsAssistantHistoryService,
    private htsChatRepository: HtsChatRepository,
    private cdr: ChangeDetectorRef,
  ) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.entrySummaryId || changes.scope || changes.isBroker) {
      this.checkVisibility();
    }
  }

  public ngOnDestroy(): void {
    this.isDestroy.next();
    this.isDestroy.complete();
  }

  public open(): void {
    if (!this.entrySummaryId) {
      return;
    }
    this.htsAssistantHistoryService.open({
      entrySummaryId: this.entrySummaryId,
      scope: this.scope,
      isDoOverToggle: this.isDoOverToggle,
    });
  }

  private checkVisibility(): void {
    this.isVisible = false;
    if (!this.entrySummaryId) {
      this.cdr.markForCheck();
      return;
    }
    this.htsChatRepository
      .getEntryHistory(this.entrySummaryId, this.scope)
      .pipe(
        catchError(() => of(null as HtsChatEntryModel)),
        take(1),
        takeUntil(this.isDestroy),
      )
      .subscribe((history: HtsChatEntryModel | null) => {
        const lines: HtsChatEntryLineModel[] = history ? history.lines || [] : [];
        const isAnyConversation: boolean = lines.some((line: HtsChatEntryLineModel) => (line.conversations || []).length > 0);
        this.isVisible = isAnyConversation || (!!history && (history.removedLines || []).length > 0);
        this.cdr.markForCheck();
      });
  }
}
